/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Button, CodeSnippet, TextArea, Toggle } from '@carbon/react';
import { Locked } from '@carbon/react/icons';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppSelector, useAppDispatch } from '@ibm-watson/shared/store';
import { ApiStatus } from '@ibm-watson/shared/ts';
import { LearnMoreLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { subscribeIntegration } from '../../../store/channels/actions';
import { selectSubscribeStatus } from '../../../store/channels/selectors';
import { SEGMENT } from '../../../trackers/trackEvents';
import { WEBCHAT_CHANNEL_TYPE } from '../../../util/channelTypes';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { sharedMessages } from '../../messages';
import { links } from '../links';
import { SecurityTab as messages } from '../messages';
import { FIELDS } from '../schema';
import { SecurityLoadingPublicKey } from './SecurityLoadingPublicKey';

import './Security.scss';

interface SecurityProps {
  assistantId?: string;
  /**
   *
   */
  tabname?: string;
  /**
   *
   */
  environmentId: string;
  /**
   *
   */
  integrationId: string;
  /**
   *
   */
  readOnly?: boolean;
}
export const Security = ({
  environmentId,
  integrationId,
  readOnly
}: SecurityProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { control, trigger } = formMethods;
  const { getFieldValue, updateFieldValue, registerInputField } =
    useFormHelpers(formMethods);

  const clientKeyChangeState = useState<boolean>(false);

  const subscribeStatus = useAppSelector(selectSubscribeStatus);

  const onToggle = async boolean => {
    ctaEvents.webChatSecurityToggle({
      category: 'Security',
      resultValue: boolean
        ? SEGMENT.EVENTS.WEB_CHAT_SECURITY_ENABLED
        : SEGMENT.EVENTS.WEB_CHAT_SECURITY_DISABLED
    });
    // If they turn security off, we blow away all the keys that are under security.
    if (!boolean) {
      updateFieldValue(FIELDS.AUTHORIZATION.CLIENT_PUBLIC_KEY, '');
      updateFieldValue(FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY, '');
    }
    setTimeout(trigger, 10);
  };

  const onGenerateKey = async () => {
    ctaEvents.webChatSecurityEncryption(
      {
        category: 'Security',
        resultValue: SEGMENT.EVENTS.WEB_CHAT_SECURITY_ENCRYPTION_ENABLED
      },
      {
        waEnvironmentId: environmentId
      }
    );
    const subscribedIBMPublicKey = await dispatch(
      subscribeIntegration({
        integrationId,
        channelType: WEBCHAT_CHANNEL_TYPE,
        data: {},
        isGenerateWebchatKey: true
      })
    );

    updateFieldValue(
      FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY,
      subscribedIBMPublicKey?.payload?.private?.authorization?.encryption
        ?.public_key?.key
    );
    updateFieldValue(
      FIELDS.AUTHORIZATION.CREATED,
      subscribedIBMPublicKey?.payload?.private?.authorization?.encryption
        ?.public_key?.created
    );
  };
  const isNewIA = useIsNewIA();
  const learnMoreLink = isNewIA
    ? links.learnMoreSecurityLinkNewIA
    : isCpd
    ? links.learnMoreSecurityCP4DLink
    : links.learnMoreSecurityLinkClassic;

  return (
    <TabContent
      ImageComponent={() => <Locked size={32} />}
      title={<FormattedMessage {...messages.title} />}
      subtitle={t(messages.subtitle)}
      learnMoreLink={learnMoreLink}>
      <Controller
        control={control}
        name={FIELDS.AUTHORIZATION.IS_ON}
        render={({ field: { onChange, value } }) => (
          <Toggle
            disabled={readOnly}
            className="TabContent__inner-field"
            id={FIELDS.AUTHORIZATION.IS_ON}
            onToggle={event => {
              onToggle(!value);
              onChange(event);
            }}
            toggled={value}
            aria-label={t(messages.enable)}
            size="sm"
          />
        )}
      />
      {getFieldValue(FIELDS.AUTHORIZATION.IS_ON) && (
        <div className="TabContent__field-container--full-width">
          <div className="TabContent__field-container">
            <h4 className="TabContent__inner-title TabContent__inner-title-heading">
              <FormattedMessage {...messages.publicKeyTitle} />
            </h4>
            <p className="TabContent__inner-field TabContent__inner-field-body">
              <FormattedMessage {...messages.publicKeyText} />
              <LearnMoreLink
                className="TabContent__learn-more-link"
                url={links.securityPublickKeyLink}
              />
            </p>
            <TextArea
              {...registerInputField({
                field: FIELDS.AUTHORIZATION.CLIENT_PUBLIC_KEY,
                labelText: messages.publicKeyLabel,
                changeState: clientKeyChangeState,
                readOnly
              })}
              className="TabContent__field TabContent__security-tab-field"
              rows={10}
            />
          </div>
          <div className="TabContent__field-container">
            <h4 className="TabContent__inner-title TabContent__inner-title-heading">
              <FormattedMessage {...messages.IBMPublicKeyTitle} />
            </h4>
            <p className="TabContent__inner-field TabContent__inner-field-body">
              <FormattedMessage {...messages.IBMPublicKeyText} />
              <LearnMoreLink
                className="TabContent__learn-more-link"
                url={links.securityIBMPublickKeyLink}
              />
            </p>
            <div className="TabContent__description">
              {!getFieldValue(FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY) && (
                <div className="IntegrationPanel-Config__security-public-key-generate-button">
                  <Button
                    id="IntegrationPanel-Config__security-public-key-generate-button"
                    kind="primary"
                    onClick={onGenerateKey}
                    disabled={subscribeStatus === ApiStatus.LOADING}>
                    <FormattedMessage
                      {...messages.IBMPublicKeyEmptyButtonTitle}
                    />
                  </Button>
                </div>
              )}
            </div>
            <div>
              {getFieldValue(FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY) && (
                <div
                  className="bx--label"
                  id="IntegrationPanel-Config__security-public-key">
                  <FormattedMessage {...messages.IBMPublicKeyLabel} />
                </div>
              )}
              <div className="TabContent__description">
                {getFieldValue(FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY) &&
                subscribeStatus !== ApiStatus.LOADING &&
                subscribeStatus !== ApiStatus.ERROR ? (
                  <CodeSnippet
                    className="TabContent__security-tab-field"
                    feedback={t(sharedMessages.copied)}
                    copyButtonDescription={t(sharedMessages.copyToClipboard)}
                    copyText={t(sharedMessages.copyToClipboard)}
                    type="multi"
                    onClick={() =>
                      copyContentToClipboard(
                        getFieldValue(FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY)
                      )
                    }>
                    {getFieldValue(FIELDS.AUTHORIZATION.IBM_PUBLIC_KEY)}
                  </CodeSnippet>
                ) : (
                  <SecurityLoadingPublicKey subscribeStatus={subscribeStatus} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </TabContent>
  );
};
